import { isWebGL2Available, useViewMode } from "@app/lib/viewMode.tsx";
import type { Manifest } from "@manifest/schema.ts";
import { Component, type ReactNode, Suspense, lazy, useEffect, useMemo, useState } from "react";
import { List } from "./List.tsx";

const Three3DLanding = lazy(() => import("./Three3DLanding.tsx"));

interface BoundaryProps {
  children: ReactNode;
  onError: (message: string) => void;
}

interface BoundaryState {
  errored: boolean;
}

class Lazy3DErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { errored: false };

  static getDerivedStateFromError(): BoundaryState {
    return { errored: true };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string }): void {
    const message = error.message || "Unknown 3D render error";
    // biome-ignore lint/suspicious/noConsole: needed to debug iOS Safari crashes
    console.error("[3D crash]", error, info.componentStack);
    this.props.onError(message);
  }

  override render(): ReactNode {
    if (this.state.errored) return null;
    return this.props.children;
  }
}

interface LandingProps {
  manifest: Manifest;
  onOpenSearch: () => void;
}

export function Landing({ manifest, onOpenSearch }: LandingProps) {
  const [mode, setMode] = useViewMode();
  const webgl = useMemo(() => isWebGL2Available(), []);
  const [showWebglError, setShowWebglError] = useState(false);
  const [crashMessage, setCrashMessage] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "3d" && !webgl) {
      setShowWebglError(true);
      setMode("2d");
    }
  }, [mode, webgl, setMode]);

  if (mode === "2d") {
    return (
      <>
        {showWebglError && (
          <div className="bg-amber-900/30 border-b border-amber-500/40 text-sm text-center py-2">
            This browser doesn't support WebGL2 — switched back to 2D view.
          </div>
        )}
        {crashMessage && (
          <div className="bg-amber-100 border-b border-amber-400 text-sm text-amber-900 py-2 px-4 text-center">
            <strong>3D view failed</strong> — switched to text. ({crashMessage})
          </div>
        )}
        <List manifest={manifest} onOpenSearch={onOpenSearch} />
      </>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="p-12 text-center text-ink-200/70 italic">
          Loading 3D world…{" "}
          <button type="button" onClick={() => setMode("2d")} className="text-chain-400 underline">
            cancel
          </button>
        </div>
      }
    >
      <Lazy3DErrorBoundary
        onError={(msg) => {
          setCrashMessage(msg);
          setMode("2d");
        }}
      >
        <Three3DLanding
          manifest={manifest}
          onContextLost={(msg) => {
            setCrashMessage(msg);
            setMode("2d");
          }}
        />
      </Lazy3DErrorBoundary>
    </Suspense>
  );
}
